import GuestRegistrationModal from '@/components/GuestRegistrationModal';
import useUser from '@/hooks/useUser';
import { Button, Grid, Text } from '@simplywallst/ui-core';
import BellRingingSolid from '@simplywallst/ui-core/icons/BellRingingSolid';
import { useCallback, useState } from 'react';
import { useReduxStateForScreener } from '@/pages/Screener/hooks/useReduxStateForScreener';
import { useTracking } from 'react-tracking';
import { getScreenerName } from '@/pages/Screener/utils';
import { useSnackbar } from '@/features/snackbar';
import { EditAndSaveScreenerModal } from '@/pages/DiscoverPageV1/components/BasicHeading/components/EditAndSaveScreenerModal/EditAndSaveScreenerModal';
import EmailAlerts from './images/email-alerts.svg';
import {
  transformUpdateCreateParams,
  extractErrorMessage,
} from '@/pages/Screener/utils';
import { useGqlCreateScreener } from '@/hooks/useGridView/useGqlCreateScreener';
import { usePageTranslation } from '@/pages/DiscoverPageV1/hooks/usePageTranslation';

type Props = {
  rules: string;
  name: string;
  description: string;
};

export const StockAlertSubscription = ({ rules, name, description }: Props) => {
  const { data: user } = useUser();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const isLoggedIn = user.loggedIn;
  const { trackEvent } = useTracking();
  const t = usePageTranslation();

  const { industryName, demonymCountry, navigateTo } =
    useReduxStateForScreener();
  const { addSnackbar, removeSnackbar } = useSnackbar();

  const { mutate: createGridGql, status: creationStatus } =
    useGqlCreateScreener({
      onCreate: ({ id, slug }) => {
        setShowSaveModal(false);

        addSnackbar({
          id: 'stock-alert-subscription-screener',
          type: 'positive',
          message: t('header.stockAlert.successMessage'),
          actionLabel: t('header.stockAlert.viewScreenerAction'),
          actionCallback: () => navigateTo(id, slug),
          secondaryActionLabel: t('header.stockAlert.dismissAction'),
          secondaryActionCallback: () =>
            removeSnackbar({ id: 'stock-alert-subscription-screener' }),
          layout: 'stack',
        });
      },
      onError: (err) => {
        const errorMessage = extractErrorMessage(err);
        addSnackbar({
          type: 'negative',
          message: errorMessage || t('header.stockAlert.errorMessage'),
          lifeTime: 3000,
        });
      },
    });

  const createGrid = useCallback(
    (params: Parameters<typeof transformUpdateCreateParams>[0]) => {
      createGridGql(transformUpdateCreateParams(params));
    },
    [createGridGql]
  );

  const screenerName = getScreenerName(name, demonymCountry, industryName);
  const defaultSaveName = t('header.stockAlert.copyOfName', {
    name: screenerName,
  });

  const handleClick = useCallback(() => {
    if (!isLoggedIn) {
      return setShowRegistrationModal(true);
    }
    setShowSaveModal(true);
  }, [isLoggedIn]);

  const isCreating = creationStatus === 'pending';
  return (
    <>
      <Button styleType="louder" alignIcon="left" onClick={handleClick}>
        <BellRingingSolid />
        {t('header.stockAlert.buttonText')}
      </Button>

      {showSaveModal && (
        <EditAndSaveScreenerModal
          title={t('header.stockAlert.modalTitle')}
          onClose={() => setShowSaveModal(false)}
          onConfirm={(viewName, viewDescription) => {
            if (!rules) return;
            trackEvent({
              action: 'click',
              subject: 'save_confirm',
            });
            createGrid({
              rules,
              name: viewName,
              description: viewDescription,
            });
          }}
          oldName={defaultSaveName}
          oldDescription={description}
          confirmLabel={t('header.stockAlert.saveAndGetAlertsButton')}
          secondaryLabel={t('header.stockAlert.cancelButton')}
          disabled={isCreating}
          primaryLoading={isCreating}
        >
          <Grid
            templateColumns="40px 1fr"
            gap="8px"
            alignItems="flex-end"
            height="100%"
          >
            <EmailAlerts />
            <Text color="darkGray" typography="xSmall">
              {t('header.stockAlert.emailUpdatesText')}
            </Text>
          </Grid>
        </EditAndSaveScreenerModal>
      )}
      {showRegistrationModal && (
        <GuestRegistrationModal
          canClose={true}
          removeWindowScroll={true}
          onClose={() => setShowRegistrationModal(false)}
          onLoginSuccess={() => setShowRegistrationModal(false)}
          onRegisterSuccess={() => setShowRegistrationModal(false)}
        />
      )}
    </>
  );
};
